import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0108 from "../lesson-08/lesson";

const linkLesson0108 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0108.id);

const theory = `<h3 class=sectionHead>Power, acoustic intensity, and sound pressure level</h3>
<p>
   The idea of expressing power on a logarithmic scale is common to many fields and is even used for wealth in common language when talking about millionaires and billionaires. For example the Richter scale expresses the scale of a earthquake as the decimal logarithm of its magnitude divided by a reference. So, the seismic waves of a magnitude 6 earthquake have an amplitude ten times greater than those of a magnitude 5 earthquake. Logarithmic scales are use in the case where the phenomenon have a large dynamic range which is the case in acoustics since we are able to ear sound waves with an amplitude from typically
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa (hearing threshold) to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>20</mn>
   </math> Pa (pain threshold). So the use of a logarithmic scale naturally imposes itself. Acousticians adopted the scale invented in the Bell laboratory for the Transmission Unit in an electrical line: the decibel (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>dB</mtext>
      </mstyle>
   </math>). The Bel is the decimal logarithm of the power divided by a reference and as its name indicate, the deciBel is a tenth of a Bel. So the power level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
         </mrow>
      </msub>
   </math>
   in decibel can be written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>lo</mi>
            <msub>
               <mrow>
                  <mi>g</mi>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mstyle mathvariant=bold-italic>
                           <mi>P</mi>
                        </mstyle>
                     </mrow>
                     <mrow>
                        <mstyle mathvariant=bold-italic>
                           <msub>
                              <mrow>
                                 <mi>P</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                        </mstyle>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <mi>P</mi>
      </mstyle>
   </math>
   is the power and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mstyle>
   </math>
   a reference power. In practice the power is not a quantity easy to access and the power can be replaced by the modulus of the acoustic intensity which is the power per unit surface. The intensity level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
   </math>
   is then defined as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>lo</mi>
            <msub>
               <mrow>
                  <mi>g</mi>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mover accent="true">
                        <mrow>
                        <mi mathvariant="bold">I</mi>
                        </mrow>
                        <mo accent=true>¯</mo>
                        </mover>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                     <mrow>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
                  </mfrac>
               </mrow>
            </mrow>




         <mo form=postfix fence=true>)</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mover accent="true">
   <mrow>
   <mi mathvariant="bold">I</mi>
   </mrow>
   <mo accent=true>¯</mo>
   </mover>
   </math>
   is the mean intensity vector and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the intensity reference. For acoustics, in air, it is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>12</mn>
         </mrow>
      </msup> </math>
    W/m
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   which correspond to the hearing threshold. Intensity is also a quantity which is not so easy to access, this is why a sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   is also defined.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>20</mn>
            <mi mathvariant=italic>lo</mi>
            <msub>
               <mrow>
                  <mi>g</mi>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>rms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the pressure reference. In air this is the sound pressure at hearing threshold which is fixed at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa. For a progressive plane wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
   </math>
   as for a progressive plane wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>rms</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ρc</mi>
         </mrow>
      </mfrac>
   </math>
   (see <a href="${linkLesson0108}" target="_blank">Lesson 1.8</a>) and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ρc</mi>
         </mrow>
      </mfrac>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ρc</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>400</mn>
   </math> Pa.s/m.
<h3 class=sectionHead>A-weighted and C-weighted dB</h3>
<p>
   In order to define a scale representative of the human perception weighted dB scales have been defined on the basis of the results of equal loudness contour which were first measured by Fletcher and Munson in 1933 and later by Robinson and Dadson in 1956, which became the basis for an ISO 226 standard. These curves show that for a same sensation of loudness more power is needed at low frequencies. This phenomenon is much more pronounced at low level. This is why different weighting are defined: A, B, C and D. B and D weighting fall into disuse and only A and C weighting can be found in sound level meters. A-weighing is for levels between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>80</mn>
   </math> dB and C-weighting for levels higher than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>80</mn>
   </math> dB. The A-weighting is very large at low frequencies and probably too much especially for levels in between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>60</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>80</mn>
   </math> dB. This make the low frequency contribution often negligible. The C-weighting is much more flat and finally not very different from the unweighted sound pressure level. Ponderation values for A and C weighting are given in <a href="#" data-img-alt="Table 1. A and C weighting" data-img-src=${config.ASSETS_URL}/01/22/theory/table-1.svg>Table 1</a> and <a href="#" data-img-alt="Figure 1. A and C weighting" data-img-src=${config.ASSETS_URL}/01/22/theory/figure-1.svg>Figure 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. A and C weighting.</figcaption>
      <img alt="Table 1. A and C weighting." src=${config.ASSETS_URL}/01/22/theory/table-1.svg>
<figure class=figure>
   <p>
      <img alt="Figure 1. A and C weighting." src=${config.ASSETS_URL}/01/22/theory/figure-1.svg>
      <figcaption class=caption>Figure 1. A and C weighting.</figcaption>
</figure>
<h3 class=sectionHead>Practicing dB scale</h3>
<p>
   In practice, it is important to acquire some familiarity with the dB scale. First of all, at least two reference values have to be known: these are the hearing threshold
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa, which corresponds to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
   </math> dB and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> Pa, which corresponds to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>94</mn>
   </math> dB. Then, the correspondance between level difference and pressure or power ratios have to be known for some values given in <a href="#" data-img-alt="Table 2. Approximate conversion from pressure and intensity to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>dB</mtext>
      </mstyle>
   </math>." data-img-src=${config.ASSETS_URL}/01/22/theory/table-2.svg>Table 2</a>.
   <figure class=float>
      <figcaption class=caption>
         Table 2. Approximate conversion from pressure and intensity to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline></math> dB.
      </figcaption>
      <img alt="Table 2. Approximate conversion from pressure and intensity to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mstyle class=text>
            <mtext>dB</mtext>
         </mstyle>
      </math>." src=${config.ASSETS_URL}/01/22/theory/table-2.svg>
   </figure>
<p>From that it is easy to convert dB values into Pascal or conversely, as in the following example
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mn>57</mn>
            <mstyle class=text>
               <mtext> dB</mtext>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mn>20</mn>
            <mo class=MathClass-bin>∗</mo>
            <mn>3</mn>
            <mo class=MathClass-bin>−</mo>
            <mn>3</mn>
            <mo class=MathClass-bin>+</mo>
            <mn>0</mn>
            <mstyle class=text>
               <mtext> dB</mtext>
            </mstyle>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>3</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>∕</mo>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
            <mo class=MathClass-bin>⋅</mo>
            <mn>2</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>5</mn>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext> Pa</mtext>
            </mstyle>
            <mo class=MathClass-rel>≈</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>014</mn>
            <mstyle class=text>
               <mtext> Pa</mtext>
            </mstyle>
         </math>
      <td class=eq-no>(4)
</table>
<p>or conversely
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>006</mn>
            <mstyle class=text>
               <mtext> Pa</mtext>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mn>3</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>3</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>⋅</mo>
            <mn>1</mn>
            <mstyle class=text>
               <mtext> Pa</mtext>
            </mstyle>
             <mo class=MathClass-bin>∕</mo>
            <mn>6</mn>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mo class=MathClass-bin>−</mo>
            <mn>60</mn>
            <mo class=MathClass-bin>+</mo>
            <mn>94</mn>
            <mstyle class=text>
               <mtext> dB</mtext>
            </mstyle>
            <mo class=MathClass-rel>≈</mo>
            <mn>50</mn>
            <mstyle class=text>
               <mtext> dB</mtext>
            </mstyle>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Be careful that, when using <a href="#" data-img-alt="Table 2. Approximate conversion from pressure and intensity to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>dB</mtext>
      </mstyle>
   </math>." data-img-src=${config.ASSETS_URL}/01/22/theory/table-2.svg>Table 2</a> they are approximation. Especially
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>10</mn>
   </math> dB do not correspond exactly to a factor of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
   </math>
   but
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msqrt>
         <mrow>
            <mn>10</mn>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>16</mn>
   </math>. So, for example, writing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>30</mn></math> dB <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mo class=MathClass-bin>×</mo>
      <mn>10</mn>
   </math> dB is dangerous as it may lead to write that this correspond to a factor of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>3</mn>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mn>27</mn>
   </math>
   for the pressure while it corresponds to a factor of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>31</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>6</mn>
   </math>. It is better to consider that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>30</mn></math> dB <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>=</mo>
      <mn>20</mn></math> dB <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mo class=MathClass-bin>+</mo>
      <mn>10</mn>
   </math> dB to avoid the accumulation of errors.
   <h3 class=sectionHead>References</h3>
  <dl>
  <dt>[1]
  <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
  <dt>[2]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[3]
  <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
  </dl>`;

export default theory;
